import { useEffect, useState } from "react";

import {
  Button,
  Field,
  HStack,
  Input,
  Select,
  Switch,
  Text,
  VStack,
} from "components/ui";
import {
  DialogActionTrigger,
  DialogBackdrop,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPositioner,
  DialogRoot,
  DialogTitle,
} from "components/ui/dialog";
import { useTranslation } from "hooks/useTranslation";
import {
  AiBudget,
  AiBudgetPeriodValue,
  aiGatewayService,
  readGatewayErrorCode,
} from "services/aiGatewayService";
import { CustomerCompany } from "services/customerCompanyService";
import { WidgetConfig } from "services/widgetService";
import { getThemeColor } from "theme/colors";

interface BudgetModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** `null` opens the form in create mode. */
  budget: AiBudget | null;
  /** Widgets this budget can be scoped to. Empty is fine — workspace-wide is always available. */
  widgets: WidgetConfig[];
  /** Customer companies this budget can be scoped to (WP-70). */
  customerCompanies: CustomerCompany[];
  onSaved: () => void;
}

type ScopeKind = "workspace" | "widget" | "customerCompany";

/** What the server applies when `thresholds` is omitted. Shown as the placeholder, not forced. */
const DEFAULT_THRESHOLDS = "50, 80, 100";

/** The whole-workspace option needs a value the native `<select>` can carry; `""` is falsy. */
const WORKSPACE_SCOPE = "workspace";

/** Mirrors `AiBudgetService.MaxNotifyEmails` — the server rejects an eleventh address. */
const MAX_NOTIFY_EMAILS = 10;

const parseThresholds = (raw: string) =>
  raw
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
    .map(Number);

/** Same separator the column uses, so what is typed here is what comes back out. */
const parseRecipients = (raw: string) =>
  raw
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part.length > 0);

/**
 * Create/edit form for a budget — an alert, or with the hard-limit switch (WP-70) an allowance
 * that refuses and pauses work once used up. Scoped to the workspace, one widget or one
 * customer company.
 *
 * Thresholds are a free-text list rather than a fixed set of checkboxes because the useful values
 * are not the same for a $10 experiment and a $5,000 production workspace — 25/50/75/90 and
 * 90/100 are both reasonable, and neither fits a preset.
 *
 * Saving replaces every field, including the ones the form did not change: the endpoint is a
 * PUT, not a patch. It does not un-fire alerts that already went out this period.
 */
const BudgetModal = ({
  isOpen,
  onClose,
  budget,
  widgets,
  customerCompanies,
  onSaved,
}: BudgetModalProps) => {
  const { t } = useTranslation();
  const isEdit = budget !== null;

  const [name, setName] = useState("");
  const [amountUsd, setAmountUsd] = useState("");
  const [period, setPeriod] = useState<AiBudgetPeriodValue>(
    AiBudgetPeriodValue.Monthly,
  );
  const [thresholds, setThresholds] = useState("");
  const [notifyEmails, setNotifyEmails] = useState("");
  const [scopeKind, setScopeKind] = useState<ScopeKind>("workspace");
  const [scope, setScope] = useState(WORKSPACE_SCOPE);
  const [customerCompanyScope, setCustomerCompanyScope] = useState("");
  const [enforceLimit, setEnforceLimit] = useState(false);
  const [isActive, setIsActive] = useState(true);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    setName(budget?.name ?? "");
    setAmountUsd(budget ? String(budget.amountUsd) : "");
    setPeriod(budget?.period ?? AiBudgetPeriodValue.Monthly);
    setThresholds(budget ? budget.thresholds.join(", ") : "");
    setNotifyEmails(budget ? budget.notifyEmails.join(", ") : "");
    setScope(budget?.widgetId ?? WORKSPACE_SCOPE);
    setCustomerCompanyScope(budget?.customerCompanyId ?? "");
    setScopeKind(
      budget?.customerCompanyId
        ? "customerCompany"
        : budget?.widgetId
          ? "widget"
          : "workspace",
    );
    setEnforceLimit(budget?.enforceLimit ?? false);
    setIsActive(budget?.isActive ?? true);
  }, [isOpen, budget]);

  const periodItems = [
    {
      value: String(AiBudgetPeriodValue.Monthly),
      label: t("app.aiGateway.budgets.periods.monthly"),
    },
    {
      value: String(AiBudgetPeriodValue.Weekly),
      label: t("app.aiGateway.budgets.periods.weekly"),
    },
    {
      value: String(AiBudgetPeriodValue.Daily),
      label: t("app.aiGateway.budgets.periods.daily"),
    },
  ];

  const scopeKindItems: { value: ScopeKind; label: string }[] = [
    { value: "workspace", label: t("app.aiGateway.budgets.fields.widgetAll") },
    { value: "widget", label: t("app.aiGateway.budgets.fields.scopeWidget") },
    {
      value: "customerCompany",
      label: t("app.aiGateway.budgets.fields.scopeCustomerCompany"),
    },
  ];

  const widgetItems = widgets
    .filter((widget) => !!widget.id)
    .map((widget) => ({ value: widget.id as string, label: widget.name }));

  const customerCompanyItems = customerCompanies.map((company) => ({
    value: company.id,
    label: company.name,
  }));

  const validate = () => {
    const next: Record<string, string> = {};
    if (!name.trim()) next.name = t("app.aiGateway.errors.budgetNameRequired");

    const amount = Number(amountUsd);
    if (!amountUsd.trim() || Number.isNaN(amount) || amount <= 0)
      next.amountUsd = t("app.aiGateway.errors.budgetAmountInvalid");

    const parsed = parseThresholds(thresholds);
    if (
      parsed.some((value) => Number.isNaN(value) || value <= 0 || value > 1000)
    )
      next.thresholds = t("app.aiGateway.errors.budgetThresholdsInvalid");

    // The server is the authority here; this only saves a round trip on the two obvious mistakes.
    const recipients = parseRecipients(notifyEmails);
    if (
      recipients.length > MAX_NOTIFY_EMAILS ||
      recipients.some((address) => !/^\S+@\S+\.\S+$/.test(address))
    )
      next.notifyEmails = t("app.aiGateway.errors.budgetNotifyEmailsInvalid");

    if (scopeKind === "widget" && scope === WORKSPACE_SCOPE)
      next.scope = t("app.aiGateway.errors.budgetScopeRequired");
    if (scopeKind === "customerCompany" && !customerCompanyScope)
      next.scope = t("app.aiGateway.errors.budgetScopeRequired");

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const parsed = parseThresholds(thresholds);
    const recipients = parseRecipients(notifyEmails);
    const payload = {
      name: name.trim(),
      amountUsd: Number(amountUsd),
      period,
      // An empty list means "use the server default", which is not the same as "no thresholds".
      thresholds: parsed.length > 0 ? parsed : null,
      widgetId:
        scopeKind === "widget" && scope !== WORKSPACE_SCOPE ? scope : null,
      customerCompanyId:
        scopeKind === "customerCompany" ? customerCompanyScope : null,
      enforceLimit,
      isActive,
      // Likewise: empty falls back to the administrators, it does not silence the alert.
      notifyEmails: recipients.length > 0 ? recipients : null,
    };

    setSaving(true);
    try {
      if (isEdit) {
        await aiGatewayService.updateBudget(budget!.id, payload);
      } else {
        await aiGatewayService.createBudget(payload);
      }
      onSaved();
      onClose();
    } catch (err: any) {
      // The interceptor already toasted this. Put the server's own verdict back on the field it
      // belongs to, so a rejection the client-side check missed is still legible.
      const code = readGatewayErrorCode(err);
      if (code === "budgetNameRequired")
        setErrors({ name: t("app.aiGateway.errors.budgetNameRequired") });
      else if (code === "budgetAmountInvalid")
        setErrors({
          amountUsd: t("app.aiGateway.errors.budgetAmountInvalid"),
        });
      else if (code === "budgetThresholdsInvalid")
        setErrors({
          thresholds: t("app.aiGateway.errors.budgetThresholdsInvalid"),
        });
      else if (code === "budgetWidgetNotFound")
        setErrors({ scope: t("app.aiGateway.errors.budgetWidgetNotFound") });
      else if (code === "budgetCustomerCompanyNotFound")
        setErrors({
          scope: t("app.aiGateway.errors.budgetCustomerCompanyNotFound"),
        });
      else if (code === "budgetScopeInvalid")
        setErrors({ scope: t("app.aiGateway.errors.budgetScopeInvalid") });
      else if (code === "budgetNotifyEmailsInvalid")
        setErrors({
          notifyEmails: t("app.aiGateway.errors.budgetNotifyEmailsInvalid"),
        });
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogRoot
      open={isOpen}
      onOpenChange={(e: { open: boolean }) => !e.open && onClose()}
      size="lg"
    >
      <DialogBackdrop />
      <DialogPositioner>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {isEdit
                ? t("app.aiGateway.budgets.modal.editTitle")
                : t("app.aiGateway.budgets.modal.createTitle")}
            </DialogTitle>
          </DialogHeader>

          <DialogBody>
            <VStack align="stretch" gap={4}>
              <Field
                label={t("app.aiGateway.budgets.fields.name")}
                required
                invalid={!!errors.name}
                errorText={errors.name}
              >
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t(
                    "app.aiGateway.budgets.fields.namePlaceholder",
                  )}
                />
              </Field>

              <HStack gap={3} align="flex-start">
                <Field
                  label={t("app.aiGateway.budgets.fields.amount")}
                  required
                  invalid={!!errors.amountUsd}
                  errorText={errors.amountUsd}
                  helperText={t("app.aiGateway.budgets.fields.amountHelp")}
                >
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={amountUsd}
                    onChange={(e) => setAmountUsd(e.target.value)}
                  />
                </Field>

                <Select
                  label={t("app.aiGateway.budgets.fields.period")}
                  items={periodItems}
                  value={[String(period)]}
                  onValueChange={(d: { value: string[] }) =>
                    setPeriod(Number(d.value[0]) as AiBudgetPeriodValue)
                  }
                  helperText={t("app.aiGateway.budgets.fields.periodHelp")}
                />
              </HStack>

              <Field
                label={t("app.aiGateway.budgets.fields.thresholds")}
                invalid={!!errors.thresholds}
                errorText={errors.thresholds}
                helperText={t("app.aiGateway.budgets.fields.thresholdsHelp")}
              >
                <Input
                  value={thresholds}
                  onChange={(e) => setThresholds(e.target.value)}
                  placeholder={DEFAULT_THRESHOLDS}
                />
              </Field>

              <Field
                label={t("app.aiGateway.budgets.fields.notifyEmails")}
                invalid={!!errors.notifyEmails}
                errorText={errors.notifyEmails}
                helperText={t("app.aiGateway.budgets.fields.notifyEmailsHelp")}
              >
                <Input
                  value={notifyEmails}
                  onChange={(e) => setNotifyEmails(e.target.value)}
                  placeholder={t(
                    "app.aiGateway.budgets.fields.notifyEmailsPlaceholder",
                  )}
                />
              </Field>

              <Select
                label={t("app.aiGateway.budgets.fields.widget")}
                items={scopeKindItems}
                value={[scopeKind]}
                onValueChange={(d: { value: string[] }) =>
                  setScopeKind((d.value[0] as ScopeKind) ?? "workspace")
                }
                errorText={scopeKind === "workspace" ? errors.scope : undefined}
                helperText={t("app.aiGateway.budgets.fields.widgetHelp")}
              />

              {scopeKind === "widget" && (
                <Select
                  label={t("app.aiGateway.budgets.fields.scopeWidget")}
                  items={widgetItems}
                  value={scope === WORKSPACE_SCOPE ? [] : [scope]}
                  onValueChange={(d: { value: string[] }) =>
                    setScope(d.value[0] ?? WORKSPACE_SCOPE)
                  }
                  errorText={errors.scope}
                />
              )}

              {scopeKind === "customerCompany" && (
                <Select
                  label={t("app.aiGateway.budgets.fields.scopeCustomerCompany")}
                  items={customerCompanyItems}
                  value={customerCompanyScope ? [customerCompanyScope] : []}
                  onValueChange={(d: { value: string[] }) =>
                    setCustomerCompanyScope(d.value[0] ?? "")
                  }
                  errorText={errors.scope}
                  helperText={t(
                    "app.aiGateway.budgets.fields.scopeCustomerCompanyHelp",
                  )}
                />
              )}

              <Switch
                checked={enforceLimit}
                onCheckedChange={(e: { checked: boolean }) =>
                  setEnforceLimit(e.checked)
                }
              >
                <VStack align="start" gap={0}>
                  <Text fontSize="sm">
                    {t("app.aiGateway.budgets.fields.enforceLimit")}
                  </Text>
                  <Text fontSize="xs" color={getThemeColor("text.secondary")}>
                    {t("app.aiGateway.budgets.fields.enforceLimitHelp")}
                  </Text>
                </VStack>
              </Switch>

              <Switch
                checked={isActive}
                onCheckedChange={(e: { checked: boolean }) =>
                  setIsActive(e.checked)
                }
              >
                <VStack align="start" gap={0}>
                  <Text fontSize="sm">
                    {t("app.aiGateway.budgets.fields.isActive")}
                  </Text>
                  <Text fontSize="xs" color={getThemeColor("text.secondary")}>
                    {t("app.aiGateway.budgets.fields.isActiveHelp")}
                  </Text>
                </VStack>
              </Switch>
            </VStack>
          </DialogBody>

          <DialogFooter>
            <HStack gap={2} justify="flex-end">
              <DialogActionTrigger asChild>
                <Button intent="secondary" size="sm" onClick={onClose}>
                  {t("app.common.cancel")}
                </Button>
              </DialogActionTrigger>
              <Button
                intent="primary"
                size="sm"
                loading={saving}
                onClick={handleSave}
              >
                {t("app.common.save")}
              </Button>
            </HStack>
          </DialogFooter>
        </DialogContent>
      </DialogPositioner>
    </DialogRoot>
  );
};

export default BudgetModal;
